import dayjs from 'dayjs';
import { ApiEndpointClient } from './resource-client';
const ORDER_API_PREFIX = 'order-api/v1';
// Preserve the selected local calendar day; toISOString() can shift it across UTC midnight.
const formatDate = (date) => dayjs(date).format('YYYY-MM-DD');
const toOrderQueryParams = (filters) => {
    const query = {};
    if (filters.assetId)
        query.asset_uuid = filters.assetId;
    if (filters.statuses?.length)
        query.status_search = filters.statuses.join(',');
    if (filters.assignedCustomers?.length)
        query.assigned_customer_users = filters.assignedCustomers.join(',');
    if (filters.titleFilter)
        query.search_string = filters.titleFilter;
    if (filters.valuationDateRange?.start)
        query.valuation_date_start = formatDate(filters.valuationDateRange.start);
    if (filters.valuationDateRange?.end)
        query.valuation_date_end = formatDate(filters.valuationDateRange.end);
    if (filters.portfolioId)
        query.portfolio_uuid = filters.portfolioId;
    return query;
};
const withCustomerId = ({ customerId, ...options } = {}) => ({
    ...options,
    ...(customerId ? { query: { customer_uuid: customerId } } : {}),
});
export class OrdersClient extends ApiEndpointClient {
    http;
    constructor(http) {
        super(http, ORDER_API_PREFIX);
        this.http = http;
    }
    getOrders = async ({ queryParams, filters, customerId, options, }) => {
        return this.get('/orders/', {
            ...options,
            query: {
                ...queryParams,
                ...(filters ? toOrderQueryParams(filters) : {}),
                ...(customerId ? { customer_uuid: customerId } : {}),
            },
        });
    };
    getOrdersWithPlausibility = async ({ filters, customerId, options, }) => {
        return this.get('/orders-with-plausibility/', {
            ...options,
            query: { ...toOrderQueryParams(filters), ...(customerId ? { customer_uuid: customerId } : {}) },
        });
    };
    getOrder = async (orderId, options) => {
        return this.get(`/orders/${orderId}`, withCustomerId(options));
    };
    deleteOrder = async ({ orderId, deleteReason, customerId, options, }) => {
        await this.delete(`/orders/${orderId}`, {
            ...withCustomerId({ ...options, customerId }),
            json: { delete_reason: deleteReason },
            response: 'raw',
        });
    };
    postOrder = async (newOrder, options) => {
        return this.post('/orders/', { ...withCustomerId(options), json: newOrder });
    };
    postExternalParticipant = async ({ orderId, newExternalParticipant, }) => {
        return this.post(`/orders/${orderId}/external-participants`, { json: newExternalParticipant });
    };
    getExternalParticipants = async (orderId, { threadId, ...options } = {}) => this.get(`/orders/${orderId}/external-participants`, {
        ...options,
        query: threadId ? { ...options.query, thread_uuid: threadId } : options.query,
        response: 'json',
    });
    deleteExternalParticipant = async ({ orderId, participantId }) => {
        await this.delete(`/orders/${orderId}/external-participants/${participantId}`, { response: 'raw' });
    };
    postImage = async (orderId, image, options) => {
        return this.postFile(`/orders/${orderId}/images`, image, withCustomerId(options));
    };
    postDocument = async (orderId, document, options) => {
        return this.postFile(`/orders/${orderId}/documents`, document, withCustomerId(options));
    };
    getDownloadAllOrderDocumentsLinkToZip = async (orderId, options) => {
        return this.get(`/orders/${orderId}/documents-attachments`, withCustomerId(options));
    };
    getDownloadAllOrderImagesLinkToZip = async (orderId, options) => {
        return this.get(`/orders/${orderId}/images-attachments`, withCustomerId(options));
    };
    patchOrder = async ({ orderId, operations, customerId, options, }) => {
        await this.patch(`/orders/${orderId}`, { ...withCustomerId({ ...options, customerId }), json: operations, response: 'raw' });
    };
}
